/**
 * Shared Category Context
 * 
 * Provides categories to all components, fetching once at app level
 * to eliminate redundant API calls
 */

import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { api, apiPaths, API_TIMEOUT_DEFAULT } from '../config/api';
const CategoryContext = createContext();

export const CategoryProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isFetching = useRef(false);

  /**
   * Fetch categories with caching
   */
  const fetchCategories = useCallback(async (forceRefresh = false) => {
    if (isFetching.current && !forceRefresh) {
      return;
    }

    isFetching.current = true;
    setLoading(true);
    setError(null);

    try {
      const response = await api.get(apiPaths.categories(), {
        timeout: API_TIMEOUT_DEFAULT,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = response.data;
      const list = Array.isArray(data)
        ? data
        : (data?.categories ?? data?.data?.categories ?? []);
      setCategories(list);
      setError(null);
    } catch (err) {
      setError(err);
      setCategories([]);
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  }, []);

  /**
   * Refresh categories
   */
  const refreshCategories = useCallback(() => {
    fetchCategories(true);
  }, [fetchCategories]);

  // Initial fetch on mount
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Listen for category updates from add/update operations
  useEffect(() => {
    const handleCategoryUpdate = () => {
      refreshCategories();
    };
    
    window.addEventListener('categoryUpdated', handleCategoryUpdate);
    return () => {
      window.removeEventListener('categoryUpdated', handleCategoryUpdate);
    };
  }, [refreshCategories]);

  const value = {
    categories,
    loading,
    error,
    refreshCategories,
    fetchCategories
  };

  return (
    <CategoryContext.Provider value={value}>
      {children}
    </CategoryContext.Provider>
  );
};

export const useCategoryContext = () => {
  const context = React.useContext(CategoryContext);
  if (!context) {
    throw new Error('useCategoryContext must be used within CategoryProvider');
  }
  return context;
};

export { CategoryContext };
